import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { listCustomFieldDefinitions } from 'api/gql-utils/employeeDataSettings/queries/listCustomFieldDefinitions';
import { getEmployeeCustomFields } from 'api/gql-utils/employeeCustomFields/queries/getEmployeeCustomFields';
import { getEffectiveAccess } from '../access';
import { decodeFieldValues } from '../validation';
import { useCurrentRole } from './useCurrentRole';
import { AccessedFieldDefinition, CustomFieldValueMap } from '../types';

export const EMPLOYEE_CUSTOM_FIELD_DEFINITIONS = 'EMPLOYEE_CUSTOM_FIELD_DEFINITIONS';
export const EMPLOYEE_CUSTOM_FIELD_VALUES = 'EMPLOYEE_CUSTOM_FIELD_VALUES';

/**
 * NOTE: the role comes from Redux loggedInUser. On legacy PHP pages the store
 * starts empty — callers there (the EMPLOYEE_OTHER_INFO widget) must dispatch
 * fetchLoggedInUser and gate rendering until the role lands, otherwise every
 * policied field computes HIDDEN.
 */
export const useEmployeeCustomFields = (userId: string | null) => {
  const { roleId, isAdmin } = useCurrentRole();

  const definitionsQuery = useQuery({
    queryKey: [EMPLOYEE_CUSTOM_FIELD_DEFINITIONS],
    queryFn: () => listCustomFieldDefinitions({}),
  });

  const valuesQuery = useQuery({
    queryKey: [EMPLOYEE_CUSTOM_FIELD_VALUES, userId],
    queryFn: () => getEmployeeCustomFields({ userId: userId as string }),
    enabled: Boolean(userId),
  });

  const definitions: AccessedFieldDefinition[] = useMemo(
    () =>
      (definitionsQuery.data?.payrollCustomFieldDefinitions?.definitions ?? [])
        .map((def) => ({ ...def, effectiveAccess: getEffectiveAccess(def, roleId, isAdmin) }))
        .filter((def) => def.effectiveAccess !== 'HIDDEN')
        // Same ordering as the settings Employee Fields page (useEmployeeFieldDefinitions).
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [definitionsQuery.data, roleId, isAdmin],
  );

  const values: CustomFieldValueMap = useMemo(
    () =>
      decodeFieldValues(
        definitions,
        (valuesQuery.data?.payrollEmployeeCustomFields?.fields as Record<string, unknown>) ?? {},
      ),
    [definitions, valuesQuery.data],
  );

  return {
    definitions,
    values,
    isLoading: definitionsQuery.isLoading || (Boolean(userId) && valuesQuery.isLoading),
    isError: definitionsQuery.isError || valuesQuery.isError,
    // Surface the underlying query error so callers can branch on the GraphQL
    // error code (e.g. FORBIDDEN → access-denied state without a Retry button).
    error: (definitionsQuery.error ?? valuesQuery.error) as unknown,
    refetch: () => {
      definitionsQuery.refetch();
      valuesQuery.refetch();
    },
  };
};
